import { Transition } from "@headlessui/react"
import { Form, Head, usePage, Link } from "@inertiajs/react"
import { useState, useEffect } from "react"
import {
  Settings,
  Mail,
  Lock,
  Monitor,
  Shield,
  Palette,
  Bell,
  Database,
  Gauge,
  CheckCircle,
  RefreshCw,
  AlertTriangle,
} from "lucide-react"

import DeleteUser from "@/components/delete-user"
import HeadingSmall from "@/components/heading-small"
import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { AppLayout } from "@/layouts/app-layout"
import { cn } from "@/lib/utils"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Badge } from "@/components/ui/badge"
import {
  settingsProfilePath,
  settingsEmailPath,
  settingsPasswordPath,
  identityEmailVerificationPath,
} from "@/routes"
import type { SharedData } from "@/types"

const settingsNavItems = [
  {
    title: "Profile",
    section: "profile",
    icon: Shield,
    description: "Update your personal information",
  },
  {
    title: "Email",
    section: "email",
    icon: Mail,
    description: "Manage your email settings",
  },
  {
    title: "Password",
    section: "password",
    icon: Lock,
    description: "Change your password",
  },
  {
    title: "Sessions",
    section: "sessions",
    icon: Monitor,
    description: "View active sessions",
  },
  {
    title: "Appearance",
    section: "appearance",
    icon: Settings,
    description: "Customize your interface",
  },
]

export default function Profile() {
  const { auth } = usePage<SharedData>().props
  const isImpersonating = auth.impersonation?.active || false
  const [activeSection, setActiveSection] = useState("profile")
  const [activeTab, setActiveTab] = useState("display")
  const [settings, setSettings] = useState<Record<string, string | boolean>>({
    theme: "auto",
    currency: "symbol",
    dateFormat: "mdy",
    numberFormat: "comma",
    portfolioAlerts: true,
    goalProgress: true,
    expenseReminders: false,
    marketUpdates: false,
    securityAlerts: true,
    autoRefresh: "5",
    dataRetention: "365",
    analytics: false,
    dataCaching: true,
    backgroundSync: true,
    dataSharing: false,
    marketingEmails: false,
    exportFormat: "csv",
  })
  const [saved, setSaved] = useState(false)

  // Get section and tab from URL params
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const section = urlParams.get("section") ?? "profile"
    const tab = urlParams.get("tab") ?? "display"
    setActiveSection(section)
    setActiveTab(tab)
  }, [])

  const handleStringSettingChange = (key: string, value: string) => {
    setSettings((prev) => ({ ...prev, [key]: value }))
    setSaved(false)
  }

  const handleBooleanSettingChange = (key: string, value: boolean) => {
    setSettings((prev) => ({ ...prev, [key]: value }))
    setSaved(false)
  }

  const handleSave = () => {
    console.log("Saving settings:", settings)
    setSaved(true)
    setTimeout(() => setSaved(false), 3000)
  }

  const handleSectionChange = (section: string) => {
    setActiveSection(section)
    // Reset tab to default when changing sections
    const defaultTab = section === "app-config" ? "display" : "display"
    setActiveTab(defaultTab)

    // Update URL without page reload
    const url = new URL(window.location.href)
    url.searchParams.set("section", section)
    if (section === "app-config") {
      url.searchParams.set("tab", defaultTab)
    } else {
      url.searchParams.delete("tab")
    }
    window.history.pushState({}, "", url.toString())
  }

  const handleTabChange = (tab: string) => {
    setActiveTab(tab)
    // Update URL without page reload
    const url = new URL(window.location.href)
    url.searchParams.set("tab", tab)
    window.history.pushState({}, "", url.toString())
  }

  const renderContent = () => {
    switch (activeSection) {
      case "profile":
        return (
          <div className="bg-card rounded-lg border p-6">
            <HeadingSmall
              title="Profile Information"
              description="Update your name and personal details"
            />

            <Form
              method="patch"
              action={settingsProfilePath()}
              options={{
                preserveScroll: true,
              }}
              className="mt-6 space-y-6"
            >
              {({ errors, processing, recentlySuccessful }) => (
                <>
                  <div className="grid gap-2">
                    <Label htmlFor="name">Name</Label>

                    <Input
                      id="name"
                      name="name"
                      className="mt-1 block w-full"
                      defaultValue={auth.user.name}
                      required
                      autoComplete="name"
                      placeholder="Full name"
                      disabled={isImpersonating}
                    />

                    <InputError className="mt-2" message={errors.name} />
                  </div>

                  <div className="flex items-center gap-4">
                    <Button disabled={processing || isImpersonating}>
                      Save
                    </Button>

                    <Transition
                      show={recentlySuccessful}
                      enter="transition ease-in-out"
                      enterFrom="opacity-0"
                      leave="transition ease-in-out"
                      leaveTo="opacity-0"
                    >
                      <p className="text-sm text-neutral-600">Saved</p>
                    </Transition>
                  </div>
                </>
              )}
            </Form>
          </div>
        )

      case "email":
        return (
          <div className="bg-card rounded-lg border p-6">
            <HeadingSmall
              title="Update Email"
              description="Update your email address and verify it"
            />

            <Form
              method="patch"
              action={settingsEmailPath()}
              options={{
                preserveScroll: true,
              }}
              className="mt-6 space-y-6"
            >
              {({ errors, processing, recentlySuccessful }) => (
                <>
                  <div className="grid gap-2">
                    <Label htmlFor="email">Email address</Label>

                    <Input
                      id="email"
                      type="email"
                      name="email"
                      className="mt-1 block w-full"
                      defaultValue={auth.user.email}
                      required
                      autoComplete="username"
                      placeholder="Email address"
                      disabled={isImpersonating}
                    />

                    <InputError className="mt-2" message={errors.email} />
                  </div>

                  {!auth.user.verified && (
                    <div>
                      <p className="text-muted-foreground -mt-4 text-sm">
                        Your email address is unverified.{" "}
                        <Link
                          href={identityEmailVerificationPath()}
                          method="post"
                          as="button"
                          className="text-foreground underline decoration-neutral-300 underline-offset-4 transition-colors duration-300 ease-out hover:decoration-current! dark:decoration-neutral-500"
                        >
                          Click here to resend the verification email.
                        </Link>
                      </p>
                    </div>
                  )}

                  <div className="flex items-center gap-4">
                    <Button disabled={processing || isImpersonating}>
                      Save
                    </Button>

                    <Transition
                      show={recentlySuccessful}
                      enter="transition ease-in-out"
                      enterFrom="opacity-0"
                      leave="transition ease-in-out"
                      leaveTo="opacity-0"
                    >
                      <p className="text-sm text-neutral-600">Saved</p>
                    </Transition>
                  </div>
                </>
              )}
            </Form>
          </div>
        )

      case "password":
        return (
          <div className="bg-card rounded-lg border p-6">
            <HeadingSmall
              title="Update Password"
              description="Ensure your account is using a long, random password to stay secure"
            />

            <Form
              method="put"
              action={settingsPasswordPath()}
              options={{
                preserveScroll: true,
              }}
              resetOnError={["password", "password_challenge"]}
              resetOnSuccess
              className="mt-6 space-y-6"
            >
              {({ errors, processing, recentlySuccessful }) => (
                <>
                  <div className="grid gap-2">
                    <Label htmlFor="password_challenge">Current password</Label>

                    <Input
                      id="password_challenge"
                      name="password_challenge"
                      type="password"
                      className="mt-1 block w-full"
                      autoComplete="current-password"
                      placeholder="Current password"
                      disabled={isImpersonating}
                    />

                    <InputError message={errors.password_challenge} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="password">New password</Label>

                    <Input
                      id="password"
                      name="password"
                      type="password"
                      className="mt-1 block w-full"
                      autoComplete="new-password"
                      placeholder="New password"
                      disabled={isImpersonating}
                    />

                    <InputError message={errors.password} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="password_confirmation">
                      Confirm password
                    </Label>

                    <Input
                      id="password_confirmation"
                      name="password_confirmation"
                      type="password"
                      className="mt-1 block w-full"
                      autoComplete="new-password"
                      placeholder="Confirm password"
                      disabled={isImpersonating}
                    />

                    <InputError message={errors.password_confirmation} />
                  </div>

                  <div className="flex items-center gap-4">
                    <Button disabled={processing || isImpersonating}>
                      Save password
                    </Button>

                    <Transition
                      show={recentlySuccessful}
                      enter="transition ease-in-out"
                      enterFrom="opacity-0"
                      leave="transition ease-in-out"
                      leaveTo="opacity-0"
                    >
                      <p className="text-sm text-neutral-600">Saved</p>
                    </Transition>
                  </div>
                </>
              )}
            </Form>
          </div>
        )

      case "sessions":
        return (
          <div className="bg-card rounded-lg border p-6">
            <HeadingSmall
              title="Sessions"
              description="Manage your active sessions across devices"
            />

            <div className="mt-6 space-y-4">
              <div className="flex flex-col space-y-2 rounded-lg border p-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <p className="font-medium">
                      Current Session
                      <Badge variant="secondary" className="ml-2">
                        Current
                      </Badge>
                    </p>
                    <p className="text-muted-foreground text-sm">
                      IP: {auth.user ? "127.0.0.1" : "Unknown"}
                    </p>
                    <p className="text-muted-foreground text-sm">
                      Active since: {new Date().toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )

      case "appearance":
        return (
          <div className="bg-card rounded-lg border p-6">
            <HeadingSmall
              title="Appearance"
              description="Customize your interface preferences"
            />

            <div className="mt-6 space-y-6">
              <div className="flex items-center justify-between space-x-4">
                <div className="flex items-center space-x-3">
                  <Palette className="text-muted-foreground h-5 w-5" />
                  <div>
                    <Label htmlFor="theme">Theme</Label>
                    <p className="text-muted-foreground text-sm">
                      Choose your preferred color scheme
                    </p>
                  </div>
                </div>
                <Select
                  value={settings.theme as string}
                  onValueChange={(value) =>
                    handleStringSettingChange("theme", value)
                  }
                >
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="light">Light</SelectItem>
                    <SelectItem value="dark">Dark</SelectItem>
                    <SelectItem value="auto">Auto (system)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )

      case "app-config":
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <HeadingSmall
                title="App Settings"
                description="Configure your FIREFlow application preferences and behavior"
              />
              <div className="flex items-center space-x-2">
                {saved && (
                  <Badge
                    variant="secondary"
                    className="bg-green-100 text-green-700"
                  >
                    <CheckCircle className="mr-1 h-3 w-3" />
                    Saved
                  </Badge>
                )}
                <Button
                  onClick={handleSave}
                  className="flex items-center space-x-2"
                >
                  <Settings className="h-4 w-4" />
                  <span>Save Changes</span>
                </Button>
              </div>
            </div>

            <Tabs
              value={activeTab}
              onValueChange={handleTabChange}
              className="space-y-6"
            >
              <TabsList className="grid w-full grid-cols-5">
                <TabsTrigger
                  value="display"
                  className="flex items-center space-x-2"
                >
                  <Palette className="h-4 w-4" />
                  <span className="hidden sm:inline">Display</span>
                </TabsTrigger>
                <TabsTrigger
                  value="notifications"
                  className="flex items-center space-x-2"
                >
                  <Bell className="h-4 w-4" />
                  <span className="hidden sm:inline">Notifications</span>
                </TabsTrigger>
                <TabsTrigger
                  value="data"
                  className="flex items-center space-x-2"
                >
                  <Database className="h-4 w-4" />
                  <span className="hidden sm:inline">Data</span>
                </TabsTrigger>
                <TabsTrigger
                  value="performance"
                  className="flex items-center space-x-2"
                >
                  <Gauge className="h-4 w-4" />
                  <span className="hidden sm:inline">Performance</span>
                </TabsTrigger>
                <TabsTrigger
                  value="privacy"
                  className="flex items-center space-x-2"
                >
                  <Shield className="h-4 w-4" />
                  <span className="hidden sm:inline">Privacy</span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="display" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Monitor className="h-5 w-5" />
                      <span>Display Preferences</span>
                    </CardTitle>
                    <CardDescription>
                      Customize how FIREFlow looks and displays information
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="flex items-center justify-between space-x-4">
                      <div className="flex items-center space-x-3">
                        <Palette className="text-muted-foreground h-5 w-5" />
                        <div>
                          <Label htmlFor="theme">Theme</Label>
                          <p className="text-muted-foreground text-sm">
                            Choose your preferred color scheme
                          </p>
                        </div>
                      </div>
                      <Select
                        value={settings.theme as string}
                        onValueChange={(value) =>
                          handleStringSettingChange("theme", value)
                        }
                      >
                        <SelectTrigger className="w-40">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="light">Light</SelectItem>
                          <SelectItem value="dark">Dark</SelectItem>
                          <SelectItem value="auto">Auto (system)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex items-center justify-between space-x-4">
                      <div className="flex items-center space-x-3">
                        <Settings className="text-muted-foreground h-5 w-5" />
                        <div>
                          <Label htmlFor="currency">Currency Display</Label>
                          <p className="text-muted-foreground text-sm">
                            How to display monetary values
                          </p>
                        </div>
                      </div>
                      <Select
                        value={settings.currency as string}
                        onValueChange={(value) =>
                          handleStringSettingChange("currency", value)
                        }
                      >
                        <SelectTrigger className="w-40">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="symbol">$1,234.56</SelectItem>
                          <SelectItem value="code">USD 1,234.56</SelectItem>
                          <SelectItem value="name">
                            US Dollar 1,234.56
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex items-center justify-between space-x-4">
                      <div className="flex items-center space-x-3">
                        <Settings className="text-muted-foreground h-5 w-5" />
                        <div>
                          <Label htmlFor="dateFormat">Date Format</Label>
                          <p className="text-muted-foreground text-sm">
                            Preferred date display format
                          </p>
                        </div>
                      </div>
                      <Select
                        value={settings.dateFormat as string}
                        onValueChange={(value) =>
                          handleStringSettingChange("dateFormat", value)
                        }
                      >
                        <SelectTrigger className="w-40">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="mdy">MM/DD/YYYY</SelectItem>
                          <SelectItem value="dmy">DD/MM/YYYY</SelectItem>
                          <SelectItem value="ymd">YYYY-MM-DD</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex items-center justify-between space-x-4">
                      <div className="flex items-center space-x-3">
                        <Settings className="text-muted-foreground h-5 w-5" />
                        <div>
                          <Label htmlFor="numberFormat">Number Format</Label>
                          <p className="text-muted-foreground text-sm">
                            How numbers are displayed
                          </p>
                        </div>
                      </div>
                      <Select
                        value={settings.numberFormat as string}
                        onValueChange={(value) =>
                          handleStringSettingChange("numberFormat", value)
                        }
                      >
                        <SelectTrigger className="w-40">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="comma">1,234,567</SelectItem>
                          <SelectItem value="space">1 234 567</SelectItem>
                          <SelectItem value="period">1.234.567</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="notifications" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Bell className="h-5 w-5" />
                      <span>Notification Preferences</span>
                    </CardTitle>
                    <CardDescription>
                      Control when and how you receive notifications
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <Settings className="text-muted-foreground h-5 w-5" />
                        <div>
                          <Label htmlFor="portfolioAlerts">
                            Portfolio Alerts
                          </Label>
                          <p className="text-muted-foreground text-sm">
                            Get notified about portfolio changes
                          </p>
                        </div>
                      </div>
                      <Switch
                        id="portfolioAlerts"
                        checked={settings.portfolioAlerts as boolean}
                        onCheckedChange={(checked) =>
                          handleBooleanSettingChange("portfolioAlerts", checked)
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <Settings className="text-muted-foreground h-5 w-5" />
                        <div>
                          <Label htmlFor="goalProgress">Goal Progress</Label>
                          <p className="text-muted-foreground text-sm">
                            Notifications when goals are reached
                          </p>
                        </div>
                      </div>
                      <Switch
                        id="goalProgress"
                        checked={settings.goalProgress as boolean}
                        onCheckedChange={(checked) =>
                          handleBooleanSettingChange("goalProgress", checked)
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <Settings className="text-muted-foreground h-5 w-5" />
                        <div>
                          <Label htmlFor="expenseReminders">
                            Expense Reminders
                          </Label>
                          <p className="text-muted-foreground text-sm">
                            Monthly expense tracking reminders
                          </p>
                        </div>
                      </div>
                      <Switch
                        id="expenseReminders"
                        checked={settings.expenseReminders as boolean}
                        onCheckedChange={(checked) =>
                          handleBooleanSettingChange(
                            "expenseReminders",
                            checked,
                          )
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <Bell className="text-muted-foreground h-5 w-5" />
                        <div>
                          <Label htmlFor="marketUpdates">Market Updates</Label>
                          <p className="text-muted-foreground text-sm">
                            Daily market summary emails
                          </p>
                        </div>
                      </div>
                      <Switch
                        id="marketUpdates"
                        checked={settings.marketUpdates as boolean}
                        onCheckedChange={(checked) =>
                          handleBooleanSettingChange("marketUpdates", checked)
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <Shield className="text-muted-foreground h-5 w-5" />
                        <div>
                          <Label htmlFor="securityAlerts">
                            Security Alerts
                          </Label>
                          <p className="text-muted-foreground text-sm">
                            Important security notifications
                          </p>
                        </div>
                      </div>
                      <Switch
                        id="securityAlerts"
                        checked={settings.securityAlerts as boolean}
                        onCheckedChange={(checked) =>
                          handleBooleanSettingChange("securityAlerts", checked)
                        }
                      />
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="data" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Database className="h-5 w-5" />
                      <span>Data Management</span>
                    </CardTitle>
                    <CardDescription>
                      Control how your data is stored and managed
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label htmlFor="data-retention">Data Retention</Label>
                          <p className="text-muted-foreground text-sm">
                            How long to keep historical data
                          </p>
                        </div>
                        <Select
                          value={settings.dataRetention as string}
                          onValueChange={(value) =>
                            handleStringSettingChange("dataRetention", value)
                          }
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="30">30 days</SelectItem>
                            <SelectItem value="90">90 days</SelectItem>
                            <SelectItem value="365">1 year</SelectItem>
                            <SelectItem value="forever">Forever</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <Label htmlFor="export-format">Export Format</Label>
                          <p className="text-muted-foreground text-sm">
                            Default format for data exports
                          </p>
                        </div>
                        <Select
                          value={settings.exportFormat as string}
                          onValueChange={(value) =>
                            handleStringSettingChange("exportFormat", value)
                          }
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="csv">CSV</SelectItem>
                            <SelectItem value="json">JSON</SelectItem>
                            <SelectItem value="xlsx">Excel</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="performance" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Gauge className="h-5 w-5" />
                      <span>Performance Settings</span>
                    </CardTitle>
                    <CardDescription>
                      Optimize app performance and data loading
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label htmlFor="auto-refresh">
                            Auto-refresh Interval
                          </Label>
                          <p className="text-muted-foreground text-sm">
                            How often to refresh market data
                          </p>
                        </div>
                        <Select
                          value={settings.autoRefresh as string}
                          onValueChange={(value) =>
                            handleStringSettingChange("autoRefresh", value)
                          }
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">1 minute</SelectItem>
                            <SelectItem value="5">5 minutes</SelectItem>
                            <SelectItem value="15">15 minutes</SelectItem>
                            <SelectItem value="60">1 hour</SelectItem>
                            <SelectItem value="off">Off</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <Label htmlFor="data-caching">Data Caching</Label>
                          <p className="text-muted-foreground text-sm">
                            Cache data for faster loading
                          </p>
                        </div>
                        <Switch
                          id="data-caching"
                          checked={settings.dataCaching as boolean}
                          onCheckedChange={(checked) =>
                            handleBooleanSettingChange("dataCaching", checked)
                          }
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <Label htmlFor="background-sync">
                            Background Sync
                          </Label>
                          <p className="text-muted-foreground text-sm">
                            Sync data in background
                          </p>
                        </div>
                        <Switch
                          id="background-sync"
                          checked={settings.backgroundSync as boolean}
                          onCheckedChange={(checked) =>
                            handleBooleanSettingChange(
                              "backgroundSync",
                              checked,
                            )
                          }
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="privacy" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Shield className="h-5 w-5" />
                      <span>Privacy & Security</span>
                    </CardTitle>
                    <CardDescription>
                      Control your data privacy and security preferences
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label htmlFor="analytics">
                            Analytics & Tracking
                          </Label>
                          <p className="text-muted-foreground text-sm">
                            Help improve FIREFlow with usage data
                          </p>
                        </div>
                        <Switch
                          id="analytics"
                          checked={settings.analytics as boolean}
                          onCheckedChange={(checked) =>
                            handleBooleanSettingChange("analytics", checked)
                          }
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <Label htmlFor="data-sharing">Data Sharing</Label>
                          <p className="text-muted-foreground text-sm">
                            Share anonymized data for research
                          </p>
                        </div>
                        <Switch
                          id="data-sharing"
                          checked={settings.dataSharing as boolean}
                          onCheckedChange={(checked) =>
                            handleBooleanSettingChange("dataSharing", checked)
                          }
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <Label htmlFor="marketing-emails">
                            Marketing Emails
                          </Label>
                          <p className="text-muted-foreground text-sm">
                            Receive updates about new features
                          </p>
                        </div>
                        <Switch
                          id="marketing-emails"
                          checked={settings.marketingEmails as boolean}
                          onCheckedChange={(checked) =>
                            handleBooleanSettingChange(
                              "marketingEmails",
                              checked,
                            )
                          }
                        />
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h4 className="text-sm font-medium">Security</h4>
                      <div className="flex flex-wrap gap-2">
                        <Button variant="outline" size="sm">
                          <Monitor className="mr-2 h-4 w-4" />
                          View Active Sessions
                        </Button>
                        <Button variant="outline" size="sm">
                          <RefreshCw className="mr-2 h-4 w-4" />
                          Generate API Key
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                        >
                          <AlertTriangle className="mr-2 h-4 w-4" />
                          Security Log
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <AppLayout>
      <Head title="Profile" />

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <HeadingSmall
            title="Profile"
            description="Manage your profile information and account settings"
          />
        </div>

        {isImpersonating && (
          <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-yellow-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  Demo Mode Active
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  You are currently viewing a demo account. Profile settings
                  cannot be modified in demo mode.
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid gap-8 lg:grid-cols-4">
          {/* Settings Navigation Sidebar */}
          <div className="space-y-6">
            <div className="bg-card rounded-lg border p-6">
              <h3 className="mb-4 text-lg font-semibold">Account Settings</h3>
              <nav className="space-y-2">
                {settingsNavItems.map((item) => {
                  const isActive = activeSection === item.section
                  return (
                    <Button
                      key={item.title}
                      size="sm"
                      variant={isActive ? "default" : "ghost"}
                      onClick={() => handleSectionChange(item.section)}
                      className={cn(
                        "h-auto w-full justify-start p-3",
                        isActive && "bg-primary text-primary-foreground",
                      )}
                    >
                      <div className="flex items-start space-x-3">
                        <item.icon className="mt-0.5 h-5 w-5 flex-shrink-0" />
                        <div className="text-left">
                          <div className="font-medium">{item.title}</div>
                          <div
                            className={cn(
                              "text-muted-foreground text-xs text-wrap",
                              isActive && "text-primary-foreground",
                            )}
                          >
                            {item.description}
                          </div>
                        </div>
                      </div>
                    </Button>
                  )
                })}
              </nav>
            </div>

            <DeleteUser isImpersonating={isImpersonating} />
          </div>

          {/* Main Content Area */}
          <div className="space-y-6 lg:col-span-3">{renderContent()}</div>
        </div>
      </div>
    </AppLayout>
  )
}
